import { useState } from 'react';
import { useContacts } from '../../lib/ContactsContext';
import { introductionsAPI } from '../../lib/api';
import { usePlatformCapabilities } from '../../hooks/usePlatformCapabilities';
import { Dialog, DialogContent } from '../ui/dialog';
import { Users } from 'lucide-react';
import { toast } from 'sonner';
import IntrosPickerStep from './intros/IntrosPickerStep';
import IntrosSendStep from './intros/IntrosSendStep';
import IntrosSmsWebConfirm from './intros/IntrosSmsWebConfirm';

/**
 * "Make intros" entry point — pill button + 2-step dialog shell.
 *
 * Step 1 (`IntrosPickerStep`): pick exactly two contacts.
 * Step 2 (`IntrosSendStep`):   confirm + send via group SMS, group email,
 *                              or per-link copy.
 *
 * This component is now intentionally thin — it owns the workflow state
 * (open / step / picked / intro / copied) and the side-effect handlers
 * (create intro, fire `sms:` / `mailto:`, clipboard) and delegates
 * presentation to the two step components above. A third small dialog
 * (`IntrosSmsWebConfirm`) handles the multi-recipient SMS "heads up"
 * notice for web users.
 *
 * The intro itself is created on Step 1→2 transition (so we have real
 * tokens to embed in delivery URLs). Re-running step 1 mints a fresh intro.
 */
export default function MakeIntrosButton({
  testId = 'home-make-intros-btn',
  className = '',
  children = null,
  ariaLabel = 'Make intros',
}) {
  const { contacts } = useContacts();
  const { caps, os } = usePlatformCapabilities();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);            // 1 = pick contacts, 2 = send
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]);       // array of contact ids, max 2
  const [creating, setCreating] = useState(false);
  const [intro, setIntro] = useState(null);
  const [copiedA, setCopiedA] = useState(false);
  const [copiedB, setCopiedB] = useState(false);
  const [smsConfirmOpen, setSmsConfirmOpen] = useState(false);

  const reset = () => {
    setStep(1);
    setSearch('');
    setPicked([]);
    setIntro(null);
    setCreating(false);
    setCopiedA(false);
    setCopiedB(false);
  };

  const onClose = (next) => {
    setOpen(next);
    if (!next) setTimeout(reset, 200);
  };

  const togglePick = (id) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev;  // hard cap at 2
      const next = [...prev, id];
      // Clear the search query after the first pick so the user can
      // immediately start typing the second contact's name from a blank
      // field (UX request from the user — was forcing manual deletion).
      if (next.length === 1) setSearch('');
      return next;
    });
  };

  const startIntro = async () => {
    if (picked.length !== 2) return;
    setCreating(true);
    try {
      const { data } = await introductionsAPI.create(picked[0], picked[1]);
      setIntro(data);
      setStep(2);
      window.dispatchEvent(new Event('intros-changed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create introduction');
    } finally {
      setCreating(false);
    }
  };

  // Build absolute URLs for embedding in SMS / email / clipboard.
  const linkA = intro ? `${window.location.origin}${intro.path_for_a}` : '';
  const linkB = intro ? `${window.location.origin}${intro.path_for_b}` : '';
  const body = intro
    ? intro.delivery.body
        .replace(intro.path_for_a, linkA)
        .replace(intro.path_for_b, linkB)
    : '';
  const subject = intro?.delivery?.subject || '';

  const fireGroupSms = () => {
    if (!intro) return;
    const recipients = (intro.delivery.recipients.phones || []).join(',');
    if (!recipients) return;
    recordAction('text');
    window.location.href = `sms:${recipients}?body=${encodeURIComponent(body)}`;
  };

  const handleSmsClick = () => {
    if (!intro) return;
    // On platforms where multi-recipient SMS is reliable (native iOS/Android
    // shells via the OS composer), fire straight away. Otherwise warn the
    // user before the messaging app opens so they know to re-add the
    // second contact if the browser drops it.
    if (caps.canSendGroupSms) {
      fireGroupSms();
      return;
    }
    setSmsConfirmOpen(true);
  };

  const sendEmail = () => {
    if (!intro) return;
    recordAction('email');
    const recipients = (intro.delivery.recipients.emails || []).join(',');
    window.location.href = `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === 'a' || which === 'b') recordAction('copy');
      if (which === 'a') { setCopiedA(true); setTimeout(() => setCopiedA(false), 2000); }
      else if (which === 'b') { setCopiedB(true); setTimeout(() => setCopiedB(false), 2000); }
      else toast.success('Copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  const recordAction = action => {
    // Advisory metadata only. Never delay a composer or claim delivery.
    if (!intro?.id) return;
    introductionsAPI.recordAction(intro.id, action)
      .then(() => window.dispatchEvent(new Event('intros-changed')))
      .catch(() => {}); // Existing/older backend or offline: retain "Created".
  };

  const pickedContacts = picked.map((id) => contacts.find((c) => c.id === id)).filter(Boolean);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className || 'inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-amber-600 dark:text-amber-300 hover:text-amber-700 dark:hover:text-amber-200 transition-colors px-2.5 py-1 rounded-full border border-amber-500/40 dark:border-amber-300/40 hover:border-amber-500/70 dark:hover:border-amber-300/70 bg-card/60 dark:bg-transparent backdrop-blur-sm'}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {children || (
          <>
            <Users size={11} />
            Make intros
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={onClose}>
        {/* Top-anchored on small screens so the search box doesn't slide
            behind the on-screen keyboard when the user starts typing a
            contact name. We override Radix's default `top-[50%]` /
            `translate-y-[-50%]` with `top-4 translate-y-0` on mobile and
            preserve the centred desktop layout from `sm:` upwards. */}
        <DialogContent
          className="max-w-md p-0 overflow-hidden top-4 translate-y-0 sm:top-[50%] sm:translate-y-[-50%]"
          data-testid="make-intros-dialog"
        >
          {step === 1 && (
            <IntrosPickerStep
              contacts={contacts}
              search={search}
              setSearch={setSearch}
              picked={picked}
              pickedContacts={pickedContacts}
              onTogglePick={togglePick}
              creating={creating}
              onContinue={startIntro}
            />
          )}

          {step === 2 && intro && (
            <IntrosSendStep
              intro={intro}
              pickedContacts={pickedContacts}
              linkA={linkA}
              linkB={linkB}
              body={body}
              copiedA={copiedA}
              copiedB={copiedB}
              onBack={() => setStep(1)}
              onCopyA={() => copy(linkA, 'a')}
              onCopyB={() => copy(linkB, 'b')}
              onSendSms={handleSmsClick}
              onSendEmail={sendEmail}
            />
          )}
        </DialogContent>
      </Dialog>

      <IntrosSmsWebConfirm
        open={smsConfirmOpen}
        onOpenChange={setSmsConfirmOpen}
        os={os}
        pickedContacts={pickedContacts}
        onCopyPhone={(phone, id) => copy(phone, `phone-${id}`)}
        onCancel={() => setSmsConfirmOpen(false)}
        onContinue={() => { setSmsConfirmOpen(false); fireGroupSms(); }}
      />
    </>
  );
}
